<template>
    <div class="settings-page">
        <template v-if="!loader">
            <!-- HEADER -->
            <div class="settings-page__header">
                <h1 class="settings-page__title">{{ $l10n.__('Réglages') }}</h1>

                <HeadingTabs
                    :tabs="[
                        { name: 'settings.home.general', label: $l10n.__('Général') },
                        { name: 'settings.home.internal', label: $l10n.__('Module Sellsy') },
                        { name: 'settings.home.external', label: $l10n.__('Module Externe') },
                    ]"
                    type="vuerouter"
                    :activeTab="state"
                    @tab-clicked="$router.push({ name: `settings.home.${$event}` })"
                />
            </div>

            <div :class="layoutClasses">
                <aside v-if="showSidebar" class="settings-page__sidebar">
                    <h3 class="settings-page__sidebar-title typographies-heading-h3">
                        {{ $t.__('settings.conformitySidebar.title') }}
                    </h3>
                    <div
                        class="settings-page__sidebar-introduction typographies-paragraph-medium-regular"
                    >
                        <img
                            src="/ressources/logged/img/illustrations/invoicing-signature-blue.svg"
                            :alt="$t.__('eInvoicing.conformityCheckListHeader.title')"
                        />
                        <p>
                            {{ $t.__('settings.conformitySidebar.description') }}
                        </p>
                    </div>
                    <CheckList
                        class="settings-page__sidebar-checklist"
                        :items="checkListItems"
                        :loading="isLoadingChecklist"
                    />
                </aside>
                <div class="settings-page__content">
                    <router-view></router-view>
                </div>
            </div>
        </template>

        <!-- LOADER -->
        <div v-else class="prelative">
            <preloader :display="loader" />
        </div>
    </div>
</template>

<script>
import Feature from 'Services/Feature';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import HeadingTabs from 'vcomponents/common/HeadingTabs/HeadingTabs.vue';
import CheckList from 'vcomponents/common/CheckList/CheckList.vue';
import { getEInvoicingStatus } from 'api/mandate';
const settingsStore = createNamespacedHelpers('settingsStore');
const { mapGetters: corpMapGetters } = createNamespacedHelpers('corp');

export default {
    components: {
        Preloader,
        HeadingTabs,
        CheckList,
    },
    data() {
        return {
            loader: true,
            isLoadingChecklist: false,
            checkListItems: [
                {
                    id: 'units_parameters',
                    label: this.$t.__('settings.conformitySidebar.checklist.units.label'),
                },
                {
                    id: 'two_factor_authentication',
                    label: this.$t.__(
                        'settings.conformitySidebar.checklist.double_authentication.label',
                    ),
                },
                {
                    id: 'vat_parameters',
                    label: this.$t.__('settings.conformitySidebar.checklist.vat_parameters.label'),
                },
                {
                    id: 'strict_conformity',
                    label: this.$t.__(
                        'settings.conformitySidebar.checklist.strict_conformity.label',
                    ),
                },
            ],
        };
    },
    computed: {
        ...settingsStore.mapGetters({
            externalPlugins: 'getExternalPlugins',
            internalPlugins: 'getInternalPlugins',
        }),
        ...corpMapGetters({
            companyInformationLocked: 'getCompanyInformationLocked',
            accountLevel: 'getAccountLevel',
        }),
        state() {
            return this.$route.meta.state;
        },
        showSidebar() {
            if (
                this.accountLevel !== 'gold' ||
                !Feature.isEnabled('facturx-show-status') ||
                this.checkListItems.length === 0
            ) {
                return false;
            }

            return this.isAdmin || this.companyInformationLocked;
        },
        layoutClasses() {
            return {
                'settings-page__layout': true,
                'settings-page__layout--has-sidebar': this.showSidebar,
            };
        },
    },
    created() {
        this.fetchMenu().finally(() => {
            this.loader = false;
        });
        this.isLoadingChecklist = true;
        if (this.showSidebar) {
            getEInvoicingStatus()
                .then((response) => {
                    this.checkListItems = this.checkListItems
                        .filter((item) => !!response.data[item.id])
                        .map((item) => {
                            const itemInfo = response.data[item.id];
                            return {
                                ...item,
                                ...itemInfo,
                            };
                        });
                })
                .catch(() => {
                    this.checkListItems = [];
                })
                .finally(() => {
                    this.isLoadingChecklist = false;
                });
        }
    },
    methods: {
        ...settingsStore.mapActions(['fetchMenu']),
    },
};
</script>

<style lang="scss" scoped>
.settings-page {
    &__header {
        align-items: center;
        display: flex;
        gap: var(--spacing-4);
        margin: 0 0 var(--spacing-4);
    }

    &__title {
        margin: 0;
    }

    &__layout {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 24px;
        max-width: 1328px;
        margin: 0 auto;

        @media only screen and (max-width: 1279px) {
            display: flex;
        }

        &--has-sidebar {
            .settings-page__content {
                grid-column: span 9;
                @media only screen and (max-width: 1279px) {
                    flex: 1;

                    &:deep(.settings-home) {
                        grid-template-columns: repeat(3, 1fr);
                    }
                    &:deep(.settings-external-plugins__list),
                    &:deep(.settings-internal-plugins__list) {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
                @media only screen and (max-width: 1079px) {
                    &:deep(.settings-home) {
                        grid-template-columns: repeat(2, 1fr);
                    }

                    &:deep(.settings-external-plugins__list),
                    &:deep(.settings-internal-plugins__list) {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }
            }
        }
    }

    &__content {
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-1);
        box-shadow: var(--elevation-1);
        flex: 1;
        grid-column: 2 / 10 span;
        padding: var(--spacing-5);
    }

    &__sidebar {
        align-self: start;
        background: var(--colors-common-background-default);
        border-radius: var(--spacing-0);
        grid-column: span 3;
        padding: var(--spacing-3);

        @media only screen and (max-width: 1279px) {
            flex-shrink: 0;
            width: 272px;
        }

        &-title {
            margin: 0 0 var(--spacing-1);
        }

        &-introduction {
            color: var(--colors-common-text-subtle-default);
            display: flex;
            gap: var(--spacing-2);
            margin-bottom: var(--spacing-4);

            p {
                margin: 0;
            }
        }
    }
}
</style>
